import { create } from 'zustand';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'sieve.theme';

/**
 * The theme the PRERENDER baked into the static HTML.
 *
 * Every public page is prerendered at build time, where there is no localStorage and no media
 * query — so the server always renders the dark variant. React's first CLIENT render has to
 * agree with that markup or hydration fails and the whole tree is regenerated (see
 * `useTheme` below), which is why this is a named constant rather than an inline fallback.
 */
export const SSR_THEME: Theme = 'dark';

/** The user's actual theme. Only meaningful in the browser. */
export function storedTheme(): Theme {
  if (typeof window === 'undefined') return SSR_THEME;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : SSR_THEME;
}

function apply(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
}

interface ThemeState {
  theme: Theme;
  toggle: () => void;
  set: (t: Theme) => void;
}

/**
 * Theme store.
 *
 * The initial value is deliberately {@link SSR_THEME}, NOT the user's stored theme.
 *
 * Reading localStorage here is the obvious thing to write and it is a hydration bug: a
 * light-mode visitor made React render the "switch to dark" toggle against server markup that
 * said "switch to light", so React threw *"the server rendered HTML didn't match the client"*
 * and discarded the tree — on every page, for every light-mode visitor. It went unnoticed
 * because a fresh browser profile has no `sieve.theme` key and therefore agrees with the
 * prerender; only someone who had actually chosen light mode ever saw it.
 *
 * So React starts from the prerendered value and `syncThemeAfterHydration()` corrects it once
 * hydration is done. The visible page colours do NOT wait for that — they are set before first
 * paint by the inline script in root.tsx, outside React entirely.
 */
export const useTheme = create<ThemeState>((set, get) => ({
  theme: SSR_THEME,
  toggle: () => {
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
    window.localStorage.setItem(STORAGE_KEY, next);
    apply(next);
    set({ theme: next });
  },
  set: (t) => {
    window.localStorage.setItem(STORAGE_KEY, t);
    apply(t);
    set({ theme: t });
  },
}));

/**
 * Point the store at the real theme once hydration has finished. Call once, from an effect.
 *
 * Uses `setState` rather than the store's own `set` so it does not write the value straight
 * back to localStorage — this is a read-back, not a user choice.
 */
export function syncThemeAfterHydration(): void {
  const actual = storedTheme();
  if (useTheme.getState().theme !== actual) useTheme.setState({ theme: actual });
}
